import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Select,
    Input,
    InputNumber,
    Form,
    Spin,
    Checkbox,
    Pagination,
    Empty,
    Alert,
    Row,
    Col,
    Tooltip
} from 'antd';
import {
    SearchOutlined,
    LoadingOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import {
    assign_unit_to_products,
    get_all_products_without_unit_details,
    get_products_with_multiple_units,
} from '../../../../Controllers/Products/Products';
import { get_all_product_units } from '../../../../Controllers/Products/Units';
import toast from 'react-hot-toast';

const { Option } = Select;

const AssignUnitModal = ({
    open,
    onClose,
    selectedProductIds = [],
    onSuccess,
    productData = {},
    unitData = {}
}) => {
    const [loading, setLoading] = useState(false);
    const [units, setUnits] = useState([]);
    const [unitLoading, setUnitLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [productLoading, setProductLoading] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState(selectedProductIds);
    const [searchText, setSearchText] = useState('');
    const [selectAllCurrentPage, setSelectAllCurrentPage] = useState(false);
    const [selectAllGlobal, setSelectAllGlobal] = useState(false);

    const [conversionValues, setConversionValues] = useState({});

    const [purchasePrice, setPurchasePrice] = useState(0);
    const [salePrice, setSalePrice] = useState(0);
    const [wholesalePrice, setWholesalePrice] = useState(0);
    const [defaultConversion, setDefaultConversion] = useState(1);

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalCount: 0,
        totalPages: 0,
        limit: 500
    });

    const [searchTimeout, setSearchTimeout] = useState(null);
    const [form] = Form.useForm();

    const fetchUnits = async () => {
        try {
            setUnitLoading(true);
            const result = await get_all_product_units({
                status: 1,
                page: 1,
                limit: 1000
            });

            if (result?.status) {
                setUnits(result?.data?.data || []);

                if (unitData?._id) {
                    setSelectedUnit(unitData._id);
                }
            } else {
                toast.error(result?.message || 'Failed to load units');
            }
        } catch (error) {
            toast.error(error.message || 'Error loading units');
        } finally {
            setUnitLoading(false);
        }
    };

    const fetchProducts = async (page = 1, search = '', isSearch = false) => {
        try {
            setProductLoading(true);

            const result = await get_all_products_without_unit_details({
                search: search,
                page: page,
                limit: pagination.limit
            });

            if (result?.status) {
                const productList = result?.data?.data || [];

                if (isSearch) {
                    setSelectedProducts(prev =>
                        prev.filter(id => productList.some(p => p._id === id))
                    );

                    const existingProductIds = productList.map(p => p._id);
                    setConversionValues(prev => {
                        const newValues = {};
                        Object.keys(prev).forEach(productId => {
                            if (existingProductIds.includes(productId)) {
                                newValues[productId] = prev[productId];
                            }
                        });
                        return newValues;
                    });
                }

                setProducts(productList);

                setPagination(prev => ({
                    ...prev,
                    currentPage: result?.data?.currentPage || page,
                    totalCount: result?.data?.totalCount || 0,
                    totalPages: result?.data?.totalPages || 0
                }));

                if (selectedProductIds.length > 0 && productData._id && page === 1) {
                    setSelectedProducts(prev => {
                        const newSelections = [...prev];
                        selectedProductIds.forEach(id => {
                            if (!newSelections.includes(id) && productList.some(p => p._id === id)) {
                                newSelections.push(id);
                            }
                        });
                        return newSelections;
                    });
                }
            } else {
                toast.error(result?.message || 'Failed to load products');
            }
        } catch (error) {
            toast.error(error.message || 'Error loading products');
        } finally {
            setProductLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            fetchProducts(1, value, true);
        }, 500);

        setSearchTimeout(timeout);
    };

    const handlePageChange = (page) => {
        fetchProducts(page, searchText);
        setSelectAllCurrentPage(false);
    };

    const handleProductSelect = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                setConversionValues(prevValues => {
                    const newValues = { ...prevValues };
                    delete newValues[productId];
                    return newValues;
                });
                return prev.filter(id => id !== productId);
            } else {
                const newSelections = [...prev, productId];
                if (!conversionValues[productId]) {
                    setConversionValues(prevValues => ({
                        ...prevValues,
                        [productId]: defaultConversion
                    }));
                }
                return newSelections;
            }
        });
    };

    const handleConversionChange = (productId, value) => {
        setConversionValues(prev => ({
            ...prev,
            [productId]: value || 1
        }));
    };

    const handleSelectAllCurrentPage = () => {
        const currentPageProductIds = products.map(product => product._id);

        if (selectAllCurrentPage) {
            setSelectedProducts(prev =>
                prev.filter(id => !currentPageProductIds.includes(id))
            );

            setConversionValues(prev => {
                const newValues = { ...prev };
                currentPageProductIds.forEach(id => {
                    delete newValues[id];
                });
                return newValues;
            });
        } else {
            const newSelections = [...selectedProducts];
            currentPageProductIds.forEach(id => {
                if (!newSelections.includes(id)) {
                    newSelections.push(id);
                }
                if (!conversionValues[id]) {
                    setConversionValues(prev => ({
                        ...prev,
                        [id]: defaultConversion
                    }));
                }
            });
            setSelectedProducts(newSelections);
        }
        setSelectAllCurrentPage(!selectAllCurrentPage);
    };

    const handleSelectAllGlobal = async () => {
        if (selectAllGlobal) {
            setSelectedProducts([]);
            setConversionValues({});
            setSelectAllGlobal(false);
        } else {
            try {
                setLoading(true);
                const result = await get_all_products_without_unit_details({
                    search: searchText,
                    page: 1,
                    limit: 10000
                });

                if (result?.status) {
                    const allProductIds = result.data.data.map(p => p._id);
                    const newConversionValues = {};

                    allProductIds.forEach(id => {
                        newConversionValues[id] = defaultConversion;
                    });

                    setSelectedProducts(allProductIds);
                    setConversionValues(newConversionValues);
                    setSelectAllGlobal(true);
                    toast.success(`Selected ${allProductIds.length} products`);
                }
            } catch (error) {
                toast.error('Failed to select all products');
            } finally {
                setLoading(false);
            }
        }
    };

    const applyDefaultConversionToAll = () => {
        const newConversionValues = { ...conversionValues };
        selectedProducts.forEach(productId => {
            newConversionValues[productId] = defaultConversion;
        });
        setConversionValues(newConversionValues);
        toast.success(`Applied conversion ${defaultConversion} to all selected products`);
    };

    const handleAssignUnit = async () => {
        if (!selectedUnit) {
            toast.error('Please select a unit');
            return;
        }

        if (selectedProducts.length === 0) {
            toast.error('Please select at least one product');
            return;
        }

        const invalidProducts = selectedProducts.filter(productId =>
            !conversionValues[productId] || conversionValues[productId] <= 0
        );

        if (invalidProducts.length > 0) {
            toast.error(`Please enter conversion values for all selected products`);
            return;
        }

        try {
            setLoading(true);

            const unitDetails = selectedProducts.map(productId => ({
                product_id: productId,
                conversion: conversionValues[productId],
                purchase_price: purchasePrice,
                sale_price: salePrice,
                wholesale_price: wholesalePrice,
                status: 1
            }));

            const data = {
                unit_id: selectedUnit,
                unit_details: unitDetails
            };

            const result = await assign_unit_to_products(data);

            if (result?.status) {
                toast.success(result.message || 'Unit assigned successfully');
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            } else {
                toast.error(result?.message || 'Failed to assign unit');
            }
        } catch (error) {
            toast.error(error.message || 'Error assigning unit');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchUnits();
            fetchProducts(1);
            form.resetFields();
            setConversionValues({});
            setDefaultConversion(1);
            setPurchasePrice(0);
            setSalePrice(0);
            setWholesalePrice(0);
        } else {
            setSelectedProducts(selectedProductIds);
            setSearchText('');
            setSelectAllCurrentPage(false);
            setSelectAllGlobal(false);
            setConversionValues({});
            setPagination({
                currentPage: 1,
                totalCount: 0,
                totalPages: 0,
                limit: 50
            });
            setSelectedUnit(null);
        }
    }, [open]);

    useEffect(() => {
        if (products.length > 0) {
            const currentPageProductIds = products.map(p => p._id);
            const allSelectedOnPage = currentPageProductIds.every(id =>
                selectedProducts.includes(id)
            );
            setSelectAllCurrentPage(allSelectedOnPage);

            if (selectedProducts.length === pagination.totalCount && pagination.totalCount > 0) {
                setSelectAllGlobal(true);
            } else {
                setSelectAllGlobal(false);
            }
        } else {
            setSelectAllCurrentPage(false);
            setSelectAllGlobal(false);
        }
    }, [selectedProducts, products, pagination.totalCount]);

    useEffect(() => {
        const newConversionValues = { ...conversionValues };
        selectedProducts.forEach(productId => {
            if (!newConversionValues[productId]) {
                newConversionValues[productId] = defaultConversion;
            }
        });

        Object.keys(newConversionValues).forEach(productId => {
            if (!selectedProducts.includes(productId)) {
                delete newConversionValues[productId];
            }
        });

        setConversionValues(newConversionValues);
    }, [selectedProducts, defaultConversion]);

    return (
        <Modal
            title="Assign Unit with Conversion Values"
            open={open}
            onCancel={onClose}
            width={1000}
            footer={[
                <Button key="close" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="assign"
                    type="primary"
                    loading={loading}
                    onClick={handleAssignUnit}
                    disabled={!selectedUnit || selectedProducts.length === 0}
                    className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82] text-white"
                >
                    Assign Unit ({selectedProducts.length.toLocaleString()} products)
                </Button>,
            ]}
        >
            <div className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Unit *
                    </label>
                    <Select
                        placeholder="Search and select unit"
                        value={selectedUnit}
                        onChange={setSelectedUnit}
                        style={{ width: '100%' }}
                        showSearch
                        allowClear
                        loading={unitLoading}
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {units.map(unit => (
                            <Option key={unit._id} value={unit._id}>
                                {unit.name}
                            </Option>
                        ))}
                    </Select>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-3">Default Values</h4>
                    <Row gutter={16}>
                        <Col span={8}>
                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                    Default Conversion *
                                    <Tooltip title="Conversion factor for this unit (e.g., 1 kg = 1000 grams)">
                                        <InfoCircleOutlined className="ml-2 text-gray-400" />
                                    </Tooltip>
                                </label>
                                <InputNumber
                                    value={defaultConversion}
                                    onChange={setDefaultConversion}
                                    min={0.001}
                                    step={0.1}
                                    precision={3}
                                    style={{ width: '100%' }}
                                    placeholder="e.g., 1"
                                    className="!border-[#21c45d] hover:!border-[#21c45d] focus:!border-[#21c45d]"

                                />
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                    Purchase Price
                                </label>
                                <InputNumber
                                    value={purchasePrice}
                                    onChange={setPurchasePrice}
                                    min={0}
                                    step={0.01}
                                    precision={3}
                                    style={{ width: '100%' }}
                                    className="!border-[#21c45d] hover:!border-[#21c45d] focus:!border-[#21c45d]"

                                />
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                    Sale Price
                                </label>
                                <InputNumber
                                    value={salePrice}
                                    onChange={setSalePrice}
                                    min={0}
                                    step={0.01}
                                    precision={3}
                                    style={{ width: '100%' }}
                                    className="!border-[#21c45d] hover:!border-[#21c45d] focus:!border-[#21c45d]"

                                />
                            </div>
                        </Col>
                    </Row>
                    <Row gutter={16} className="mt-2">
                        <Col span={8}>
                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                    Wholesale Price
                                </label>
                                <InputNumber
                                    value={wholesalePrice}
                                    onChange={setWholesalePrice}
                                    min={0}
                                    step={0.01}
                                    precision={3}
                                    style={{ width: '100%' }}
                                    className="!border-[#21c45d] hover:!border-[#21c45d] focus:!border-[#21c45d]"

                                />
                            </div>
                        </Col>
                        <Col span={16}>
                            <div className="flex items-end h-full">
                                <Button
                                    type="dashed"
                                    onClick={applyDefaultConversionToAll}
                                    disabled={selectedProducts.length === 0}
                                    className="!border-[#21c45d] !text-[#21c45d] hover:!border-[#38cb82] hover:!text-[#38cb82]"
                                >
                                    Apply Default Conversion to All Selected
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Select Products
                                <span className="ml-2 text-sm font-normal text-gray-500">
                                    ({selectedProducts.length.toLocaleString()} selected of {pagination.totalCount.toLocaleString()} total)
                                </span>
                            </label>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                                <Checkbox
                                    checked={selectAllCurrentPage}
                                    onChange={handleSelectAllCurrentPage}
                                    disabled={products.length === 0}
                                >
                                    <span className="text-sm">Select Page ({products.length})</span>
                                </Checkbox>
                            </div>
                            <div className="flex items-center">
                                <Checkbox
                                    checked={selectAllGlobal}
                                    onChange={handleSelectAllGlobal}
                                    disabled={pagination.totalCount === 0}
                                >
                                    <span className="text-sm">Select All ({pagination.totalCount.toLocaleString()})</span>
                                </Checkbox>
                            </div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <Input
                            placeholder="Search products by name or SKU..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                            size="large"
                            className="!border-[#21c45d] hover:!border-[#21c45d] focus:!border-[#21c45d]"
                        />
                    </div>

                    <div className="border rounded-lg">
                        <div className="flex justify-between items-center p-3 bg-gray-50 border-b">
                            <div className="text-sm text-gray-600">
                                Showing {(pagination.currentPage - 1) * pagination.limit + 1}-
                                {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                                of {pagination.totalCount.toLocaleString()} products
                            </div>
                            {pagination.totalPages > 1 && (
                                <div className="flex items-center">
                                    <Pagination
                                        current={pagination.currentPage}
                                        total={pagination.totalCount}
                                        pageSize={pagination.limit}
                                        onChange={handlePageChange}
                                        showSizeChanger={false}
                                        showQuickJumper
                                        size="small"
                                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {productLoading ? (
                                <div className="p-8 text-center">
                                    <Spin
                                        indicator={<LoadingOutlined style={{ fontSize: 24, color: '#21c45d' }} spin />}
                                        size="large"
                                    />
                                    <p className="text-gray-500 mt-4">Loading products...</p>
                                </div>
                            ) : products.length === 0 ? (
                                <div className="p-8 text-center">
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            searchText
                                                ? `No products found matching "${searchText}"`
                                                : "No products found"
                                        }
                                    />
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {products.map(product => {
                                        const isSelected = selectedProducts.includes(product._id);
                                        const currentConversion = conversionValues[product._id] || defaultConversion;

                                        return (
                                            <div
                                                key={product._id}
                                                className={`p-3 hover:bg-gray-50 flex items-start ${isSelected ? 'bg-green-50 border-l-4 border-green-500' : ''
                                                    }`}
                                            >
                                                <div className="flex items-start w-full">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() => handleProductSelect(product._id)}
                                                        className="mr-4 mt-1"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-gray-900 truncate">
                                                            {product.name}
                                                        </div>
                                                        <div className="text-sm text-gray-500 mt-1">
                                                            <span className="mr-4">SKU: {product.ref || 'N/A'}</span>
                                                            <span className="mr-4">Stock: {product.stock?.toFixed?.(3) || 0}</span>
                                                            {product.unit && (
                                                                <span>Current Unit: {product.unit.name}</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {isSelected && (
                                                        <div className="ml-4 w-48">
                                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                                Conversion *
                                                                <Tooltip title="Conversion factor for this product (e.g., 1 kg = 1000 grams)">
                                                                    <InfoCircleOutlined className="ml-1 text-gray-400" />
                                                                </Tooltip>
                                                            </label>
                                                            <InputNumber
                                                                value={currentConversion}
                                                                onChange={(value) => handleConversionChange(product._id, value)}
                                                                min={0.001}
                                                                step={0.1}
                                                                precision={3}
                                                                style={{ width: '100%' }}
                                                                size="small"
                                                                placeholder="Conversion"
                                                                className="!border-[#21c45d] hover:!border-[#21c45d] focus:!border-[#21c45d]"

                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="flex justify-between items-center p-3 bg-gray-50 border-t">
                                <div className="text-sm text-gray-600">
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </div>
                                <div className="flex space-x-2">
                                    <Button
                                        size="small"
                                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                                        disabled={pagination.currentPage === 1}
                                        type="primary"
                                        className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82] text-white"

                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                                        disabled={pagination.currentPage === pagination.totalPages}
                                        type="primary"
                                        className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82] text-white"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {selectedProducts.length > 0 && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex justify-between items-center">
                            <div>
                                <h4 className="font-medium text-green-800 mb-1">Summary</h4>
                                <p className="text-green-700">
                                    <strong>{selectedProducts.length.toLocaleString()}</strong> product(s) selected
                                </p>
                                <div className="text-sm text-green-600 mt-1">
                                    <p>• Will create {selectedProducts.length.toLocaleString()} unit detail records</p>
                                    <p>• Each product will have conversion value entered</p>
                                    <p>• Prices will be applied to all selected products</p>
                                </div>
                            </div>
                            {selectedProducts.length > 500 && (
                                <div className="text-orange-600 bg-orange-50 px-3 py-1 rounded">
                                    <strong>Note:</strong> Bulk operation in progress
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {selectedProducts.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                        <div className="flex items-start">
                            <ExclamationCircleOutlined className="text-yellow-500 mr-2 mt-0.5" />
                            <div className="text-yellow-800">
                                <p className="font-medium">Important: Conversion Values Required</p>
                                <p className="text-sm">
                                    Each selected product must have a conversion value.
                                    You can set a default value above and apply it to all, or enter individual values.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default AssignUnitModal;